// contexts/user-chat.tsx
import { createContext, ReactNode, useReducer } from "react"
import { type Message } from "ai/react"

export interface AgentConfig {
  topP: string
  model: string
  tokens: string
  temperature: string
}

export interface Agent {
  short_name: string
  config?: AgentConfig
  files?: File[]
  system_profile?: string
  answer_template?: string
}

export interface ColorScheme {
  bgColor: string
  textColor: string
}

export interface ChatConfig {
  id?: string
  user_id?: string
  organization_id?: string
  logo?: string | null
  ai_name?: string | null
  description?: string | null
  ai_avatar?: string | null
  color_scheme: ColorScheme
  text_placeholder?: string | null
  agent?: Agent | null
  show_sources?: boolean | false
  live?: boolean
}

interface ImageUrlsContent {
  url: string
}

interface ImageUrls {
  [customKey: string]: ImageUrlsContent[]
}

interface State {
  userInputValue: string
  submitForm: boolean
  config: ChatConfig | null
  messageMetadata: { [key: string]: any }
  submitDisabled: boolean
  sandbox?: boolean
  triggerReset: boolean
  token?: string | null
  imageUrls?: ImageUrls
  messages?: Message[]
}

const initialState: State = {
  userInputValue: "",
  submitForm: false,
  config: null,
  messageMetadata: {},
  submitDisabled: false,
  sandbox: false,
  triggerReset: true,
  token: null,
  imageUrls: {},
  messages: [],
}

type Action =
  | { type: "SET_USER_INPUT_VALUE"; payload: string }
  | { type: "SET_SUBMIT_FORM"; payload: boolean }
  | { type: "SET_CONFIG"; payload: ChatConfig | null }
  | { type: "SET_MESSAGE_METADATA"; payload: { [key: string]: any } }
  | { type: "SET_SUBMIT_DISABLED"; payload: boolean }
  | { type: "SET_SANDBOX"; payload: boolean }
  | { type: "SET_TRIGGER_RESET"; payload: boolean }
  | { type: "SET_TOKEN"; payload: string }
  | { type: "SET_IMAGE_URLS"; payload: ImageUrls }
  | { type: "SET_MESSAGES"; payload: Message[] }

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case "SET_USER_INPUT_VALUE":
      return { ...state, userInputValue: action.payload }
    case "SET_SUBMIT_FORM":
      return { ...state, submitForm: action.payload }
    case "SET_CONFIG":
      return {
        ...state,
        config: action.payload,
      }
    case "SET_MESSAGE_METADATA":
      return {
        ...state,
        messageMetadata: action.payload,
      }
    case "SET_SUBMIT_DISABLED":
      return {
        ...state,
        submitDisabled: action.payload,
      }
    case "SET_SANDBOX":
      return {
        ...state,
        sandbox: action.payload,
      }
    case "SET_TRIGGER_RESET":
      return {
        ...state,
        triggerReset: action.payload,
      }
    case "SET_TOKEN":
      return {
        ...state,
        token: action.payload,
      }
    case "SET_IMAGE_URLS":
      return {
        ...state,
        imageUrls: action.payload,
      }
    case "SET_MESSAGES":
      return {
        ...state,
        messages: action.payload,
      }

    default:
      return state
  }
}

export const UserChatContext = createContext<{
  state: State
  dispatch: React.Dispatch<Action>
}>({
  state: initialState,
  dispatch: () => undefined,
})

interface UserChatProviderProps {
  children: ReactNode
}

export function UserChatProvider({ children }: UserChatProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState)

  return (
    <UserChatContext.Provider value={{ state, dispatch }}>
      {children}
    </UserChatContext.Provider>
  )
}
